/**
 * The History popup's Correlation tab: the family insight table, the four
 * seeded historical-accuracy events and the 180-day synthetic lead/lag
 * series the chart draws. Port of visualizer.js getCorrelationInfo,
 * generateComparisonData and the data half of drawCorrelationGraph; the
 * seeds come from the ids so the output is stable per sensor.
 */
import { hashIds } from './componentPrediction';

export interface CorrelationInfo {
  leadIndicator: string;
  lagIndicator: string;
  leadTime: string;
  coefficient: number;
  description: string;
}

/** Family insight by keyword in the data id or subsystem id (fan, motor/antenna, HPA, LNA, modem, default). */
export function getCorrelationInfo(dataId: string | null | undefined, subsystemId: string | null | undefined): CorrelationInfo {
  const idLower = (dataId || '').toLowerCase();
  const subLower = (subsystemId || '').toLowerCase();

  if (
    idLower.indexOf('rpm') !== -1 ||
    idLower.indexOf('speed') !== -1 ||
    subLower.indexOf('fan') !== -1 ||
    subLower.indexOf('cooling') !== -1
  ) {
    return {
      leadIndicator: 'Temperature Rise',
      lagIndicator: 'RPM Decline',
      leadTime: '30-60 days',
      coefficient: 0.87,
      description: 'Bearing wear causes temperature rise before RPM drops',
    };
  }
  if (
    idLower.indexOf('current') !== -1 ||
    subLower.indexOf('motor') !== -1 ||
    subLower.indexOf('antenna') !== -1 ||
    subLower.indexOf('az') !== -1 ||
    subLower.indexOf('el') !== -1
  ) {
    return {
      leadIndicator: 'Motor Current',
      lagIndicator: 'Position Error',
      leadTime: '14-45 days',
      coefficient: 0.82,
      description: 'Increased current draw indicates bearing wear',
    };
  }
  if (
    subLower.indexOf('hpa') !== -1 ||
    subLower.indexOf('sspa') !== -1 ||
    idLower.indexOf('power') !== -1 ||
    idLower.indexOf('efficiency') !== -1
  ) {
    return {
      leadIndicator: 'Temperature',
      lagIndicator: 'Output Power',
      leadTime: '45-90 days',
      coefficient: 0.79,
      description: 'Thermal degradation reduces power output',
    };
  }
  if (subLower.indexOf('lna') !== -1 || idLower.indexOf('noise') !== -1) {
    return {
      leadIndicator: 'Noise Figure',
      lagIndicator: 'Signal Strength',
      leadTime: '60-120 days',
      coefficient: 0.75,
      description: 'Rising noise figure precedes signal degradation',
    };
  }
  if (subLower.indexOf('modem') !== -1 || idLower.indexOf('ber') !== -1 || idLower.indexOf('lock') !== -1) {
    return {
      leadIndicator: 'Eb/N0',
      lagIndicator: 'Lock Status',
      leadTime: '1-7 days',
      coefficient: 0.91,
      description: 'Eb/N0 decline predicts signal loss',
    };
  }
  return {
    leadIndicator: 'Temperature',
    lagIndicator: 'Health',
    leadTime: '60-90 days',
    coefficient: 0.85,
    description: 'Temperature rise is a common failure precursor',
  };
}

/** "<lead> changes precede failures by <lead time>". */
export function correlationSubtitle(info: CorrelationInfo): string {
  return info.leadIndicator + ' changes precede failures by ' + info.leadTime;
}

/** The original's linear congruential generator; callers seed it with the absolute id hash so every draw is in 0 to 1 (a negative seed would draw negative numbers until it wrapped). */
export function createSeededRandom(initialSeed: number): () => number {
  let seed = initialSeed;
  return () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
}

export interface ComparisonRow {
  event: string;
  /** YYYY-MM-DD. */
  predicted: string;
  actual: string;
  /** "+N days" or "-N days". */
  variance: string;
  varianceDays: number;
}

/** Component family name for the accuracy rows. */
export function comparisonComponentName(dataId: string | null | undefined, subsystemId: string | null | undefined): string {
  const idLower = (dataId || '').toLowerCase();
  const subLower = (subsystemId || '').toLowerCase();
  if (subLower.indexOf('fan') !== -1 || subLower.indexOf('cooling') !== -1 || idLower.indexOf('rpm') !== -1) return 'Cooling Fan';
  if (subLower.indexOf('lna') !== -1 || idLower.indexOf('noise') !== -1 || idLower.indexOf('gain') !== -1) return 'LNA Module';
  if (subLower.indexOf('hpa') !== -1 || subLower.indexOf('sspa') !== -1 || idLower.indexOf('power') !== -1) return 'HPA/SSPA';
  if (subLower.indexOf('motor') !== -1 || subLower.indexOf('az') !== -1 || subLower.indexOf('el') !== -1) return 'Drive Motor';
  if (subLower.indexOf('modem') !== -1 || idLower.indexOf('lock') !== -1 || idLower.indexOf('ber') !== -1) return 'Modem';
  if (idLower.indexOf('temp') !== -1) return 'Thermal System';
  return 'Component';
}

/**
 * Four seeded events, 2023 to 2026: a predicted local date (month 1 to 12
 * and day 1 to 28 from the generator) and an actual date within ±25 days,
 * both as UTC date strings. Seeded with the absolute id hash (defect fix,
 * see PORT_NOTES.md).
 */
export function generateComparisonData(dataId: string | null | undefined, subsystemId: string | null | undefined): ComparisonRow[] {
  const seedRandom = createSeededRandom(Math.abs(hashIds(dataId || '', subsystemId || '')));
  const componentName = comparisonComponentName(dataId, subsystemId);
  const events: ComparisonRow[] = [];
  const baseYear = 2023;
  for (let i = 0; i < 4; i++) {
    const year = baseYear + i;
    const month = Math.floor(seedRandom() * 12) + 1;
    const day = Math.floor(seedRandom() * 28) + 1;
    const predictedDate = new Date(year, month - 1, day);
    const predictedStr = predictedDate.toISOString().split('T')[0];
    const variance = Math.floor((seedRandom() - 0.5) * 50);
    const actualDate = new Date(predictedDate);
    actualDate.setDate(actualDate.getDate() + variance);
    const actualStr = actualDate.toISOString().split('T')[0];
    events.push({
      event: componentName + ' #' + (i + 1),
      predicted: predictedStr,
      actual: actualStr,
      variance: variance >= 0 ? '+' + variance + ' days' : variance + ' days',
      varianceDays: variance,
    });
  }
  return events;
}

export interface CorrelationSeries {
  info: CorrelationInfo;
  /** 180 daily values; the lead indicator drifts up after day 90. */
  leadData: number[];
  /** 180 daily values; health, 0 to 100, following the lead after the lead time. */
  lagData: number[];
  /** First number of the family's lead time. */
  leadTimeDays: number;
  /** The dashed marker day. */
  peakDay: number;
  days: number;
}

/** The chart's synthetic lead/lag series, seeded by the absolute id hash. */
export function generateCorrelationSeries(dataId: string | null | undefined, subsystemId: string | null | undefined): CorrelationSeries {
  const info = getCorrelationInfo(dataId, subsystemId);
  const seedRandom = createSeededRandom(Math.abs(hashIds(dataId || '', subsystemId || '')));
  const days = 180;
  const leadData: number[] = [];
  const lagData: number[] = [];

  let leadTimeDays = 60;
  const leadTimeMatch = info.leadTime.match(/(\d+)/);
  if (leadTimeMatch) leadTimeDays = parseInt(leadTimeMatch[1], 10);

  for (let i = 0; i < days; i++) {
    const baseline = 35 + seedRandom() * 10;
    const lead = baseline + Math.sin(i / 30) * 3 + (i > 90 ? (i - 90) * 0.15 : 0) + seedRandom() * 2;
    leadData.push(lead);
    const lagIdx = Math.max(0, i - leadTimeDays);
    let lag = 100 - (leadData[lagIdx] > 40 ? (leadData[lagIdx] - 40) * 8 * info.coefficient : 0);
    lag = Math.max(0, Math.min(100, lag + seedRandom() * 5));
    lagData.push(lag);
  }
  return { info, leadData, lagData, leadTimeDays, peakDay: 120, days };
}
